import { useMutation } from "@apollo/client";
import { MenuDivider, MenuItem } from "@szhsin/react-menu";
import { TbPlaylistX } from "react-icons/tb";
import { useLocation } from "react-router-dom";
import {
  AlignGoalMutationMutation,
  AlignGoalMutationMutationVariables,
  ArtifactComponentFragmentFragment,
  BasicGoalArtifactComponentFragmentFragment,
  GoalArtifactSidebarFragmentFragment,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { refreshAlignmentOfGoalIds } from "@apps/goal-alignment/helpers";
import ArtifactEmbedCard from "@components/wysiwyg/extensions/artifacts/artifact-embed-card";
import { graphqlNone } from "@helpers/constants";
import {
  getUrl,
  isPrivateBasicGoalArtifactNode,
  toWithBackground,
} from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import alignGoalMutation from "./graphql/align-goal-mutation";
import getArtifactSidebarQuery from "./graphql/get-artifact-sidebar-query";

const ArtifactSidebarAlignedGoalItem = ({
  artifact,
  alignedGoal,
  alignmentType,
}: {
  artifact: GoalArtifactSidebarFragmentFragment;
  alignedGoal:
    | GoalArtifactSidebarFragmentFragment
    | BasicGoalArtifactComponentFragmentFragment
    | (ArtifactComponentFragmentFragment & {
        __typename: "GoalArtifactNode";
      });
  alignmentType?: "parent" | "child" | "ancestor";
}) => {
  const location = useLocation<TFLocationState>();
  const { confirm, ConfirmationDialog } = useConfirm(
    "Are you sure you want to remove this alignment?",
    "",
  );
  const [updateGoal] = useMutation<
    AlignGoalMutationMutation,
    AlignGoalMutationMutationVariables
  >(alignGoalMutation);

  const handleClearAlignment = async () => {
    if (alignmentType !== "parent" && alignmentType !== "child") return;
    const confirmed = await confirm();
    const goalIdsToRefresh = [
      artifact.parentGoalId,
      alignedGoal.parentGoalId,
      artifact.id,
      alignedGoal.id,
    ];
    if (confirmed) {
      updateGoal({
        variables: {
          goalId: alignmentType === "parent" ? artifact.id : alignedGoal.id,
          parentGoalId: graphqlNone,
        },
        refetchQueries: [getArtifactSidebarQuery],

        onCompleted: () => {
          refreshAlignmentOfGoalIds(goalIdsToRefresh);
        },
      });
    }
  };

  const isClickable = !isPrivateBasicGoalArtifactNode(alignedGoal);
  return (
    <>
      <ConfirmationDialog />
      {/* Same embed card component as artifacts in meeting/document notes,
          so goals render identically in both places. */}
      <ArtifactEmbedCard
        key={alignedGoal.id}
        artifact={alignedGoal}
        to={
          isClickable
            ? toWithBackground({
                pathname: getUrl({
                  artifactId: alignedGoal.id,
                  artifactType: alignedGoal.artifactType,
                }),
                location,
              })
            : undefined
        }
        hideDropdown={alignedGoal.__typename === "BasicGoalArtifactNode"}
        dropdownChildren={
          (alignmentType === "child" || alignmentType === "parent") &&
          artifact.canUpdate.permission ? (
            <>
              <MenuItem onClick={handleClearAlignment}>
                <TbPlaylistX className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />{" "}
                Remove alignment
              </MenuItem>
              <MenuDivider />
            </>
          ) : undefined
        }
      />
    </>
  );
};

export default ArtifactSidebarAlignedGoalItem;
